import { action, computed, makeObservable, observable } from "mobx";
import utils from "../utils";
import environment from "../environment";
import { simpleClone } from "../helpers";
import { controlCenter } from "@ventionco/mm-machine-logic-robot";
import { Sequence } from "./Sequence";
import { ApplicationParameters } from "./ApplicationParameters";
import { ApplicationBase } from "./ApplicationBase";
import {
  ApplicationState,
  ApplicationTypes,
  SequenceExecutionStateString,
  SequenceState,
  InstructionStateStatus,
} from "./constants";
import { InstructionCategory } from "./instruction_types";

const { utils: { ArrayOfArrayReferenceFrame__fromArrayOfPoint2DxExecutionReferenceFrame, machineLogicApplicationRobotParameters_updateWithRobotAppsMachineContext } } = controlCenter;

/**
 * Represents a machine logic application.

 */
export class Application extends ApplicationBase {
  /**
   * @param {Library} pLibrary The machine logic library this application is part of.
   * @param {ApplicationTypes} pType Type of the Application. Defaults to ApplicationTypes.MachineLogic.
   * @param {boolean} pIsValidateable If set to true, the Validator will validate the Application. Defaults to "true".
   * @param {boolean} pIsPlayable If set to true, this application will appear in the app launcher and UI builder. Defualts to "true".
   */
  constructor(pLibrary, pType = ApplicationTypes.MachineLogic, pIsValidateable = true, pIsPlayable = true) {
    super(pLibrary, pType, pIsValidateable, pIsPlayable);
    this.mVersion = 1;
    this.mSequences = [];
    this.mInitialSequence = undefined;
    this.mBreakpoints = {};
    this.mParameters = {};
    this.autoLaunch = false;
    this.mCurrentSequence = undefined;

    makeObservable(this, {
      mCurrentSequence: observable,
      mParameters: observable,
      mInitialSequence: observable,
      mSequences: observable,
      validationErrorCount: computed,
      parameters: computed,
      currentSequence: computed,
      invalidInstructionUuids: computed,
      initialSequence: computed,
      sequences: computed,
      setParameters: action,
      setAppParameters: action,
      addParameters: action,
      addApplicationParameter: action,
      updateApplicationParameter: action,
      removeApplicationParameter: action,
      selectSequenceByName: action,
      setInitialSequence: action,
      addSequence: action,
      newSequence: action,
      setRobotParametersClips: action,
    });
  }

  setDirty(pCustomTimeOut, pBeforeDeploy = null) {
    super.setDirty(pCustomTimeOut, () => {
      this.setVersionHash();
      pBeforeDeploy && pBeforeDeploy();
    });
  }

  setState(executionState) {
    super.setState(executionState);
    if (executionState === null || executionState === undefined) return;
    this.updateSequenceStates(executionState.sequences);
    this.updateInstructionStates(executionState.sequences, executionState.fsmStates);
  }

  updateSequenceStates(sequenceExecutionState) {
    const app = this;
    for (const [sequenceName, executionState] of Object.entries(sequenceExecutionState)) {
      const sequence = app.getSequence(sequenceName);

      if (sequence === undefined) {
        console.error(`Sequence not found in execution state`);
        return; //HACK for PATH-FOLLOWING-VENTION-IO. should be changed on prod too because always breaks on edge
      }

      if (executionState === SequenceExecutionStateString.NOT_STARTED || executionState === SequenceExecutionStateString.COMPLETED) {
        sequence.setState(SequenceState.INACTIVE);
      }
      else if (typeof executionState === "number") {
        if (app.mState === ApplicationState.RUNNING) {
          const currentInstruction = sequence.instructions[executionState];
          if(currentInstruction && currentInstruction.isWaitingInstruction()) {
            sequence.setState(SequenceState.WAITING);
          } else {
            sequence.setState(SequenceState.RUNNING);
          }
        }
        else if (app.mState === ApplicationState.PAUSED) {
          sequence.setState(SequenceState.PAUSED);
        }
      }
    }
  }

  updateInstructionStates(sequenceExecutionState, fsmExecutionState) {
    this.mSequences.forEach((sequence) => {
      if (sequence.state === SequenceState.INACTIVE) {
        sequence.instructions.forEach((instruction) => instruction.setState(InstructionStateStatus.INACTIVE));
        return;
      }

      const activeInstructionIndex = sequenceExecutionState[sequence.name];
      sequence.instructions.forEach((instruction, instructionIndex) => {
        // default to inactive
        let instructionState = InstructionStateStatus.INACTIVE;

        if (instruction.category === InstructionCategory.ROBOT) {
          return;
        }

        if(instruction.category === InstructionCategory.FSM) {
          this.updateFSMInstructionState(instruction, fsmExecutionState);
          return;
        }

        if(instruction.category === InstructionCategory.CONDITION) {
          this.updateConditionInstructionState(instruction, fsmExecutionState);
          return;
        }

        if (instructionIndex === activeInstructionIndex) {
          if (sequence.state === SequenceState.PAUSED) {
            instructionState = InstructionStateStatus.PAUSED;
          }
          else if (instruction.isWaitingInstruction()){
            instructionState = InstructionStateStatus.WAITING;
          }
          else {
            instructionState = InstructionStateStatus.RUNNING;
          }
        }
        instruction.setState(instructionState);
      });
    });
  }

  updateConditionInstructionState (conditionInstruction) {
    // default condition instruction state
    const instructionState = {
      status: InstructionStateStatus.INACTIVE,
      subInstructionState: {
        activeSequence: undefined
      }
    };

    const activeSequence = conditionInstruction.parameters.conditions
      .map(condition => condition.sequenceName)
      .find(sequenceName => [SequenceState.RUNNING, SequenceState.WAITING, SequenceState.PAUSED].includes(this.getSequence(sequenceName).state));

    if (activeSequence === undefined) {
      conditionInstruction.setState(instructionState);
      return;
    }

    instructionState.subInstructionState.activeSequence = activeSequence;
    instructionState.status = this.getSequence(activeSequence).state;

    conditionInstruction.setState(instructionState);
  }
  updateFSMInstructionState (fsmInstruction, fsmExecutionState) {

    // default fsm state
    const instructionState = {
      status: InstructionStateStatus.INACTIVE,
      subInstructionState: {
        activeStateName: undefined,
      }
    };

    if (this.state !== ApplicationState.RUNNING) {
      fsmInstruction.setState(instructionState);
      return;
    }

    const fsmStateName = fsmExecutionState[fsmInstruction.parameters.name];
    const activeState = fsmInstruction.parameters.states.find(state => state.stateName === fsmStateName);

    // No active state
    if (activeState === undefined) {
      fsmInstruction.setState(instructionState);
      return;
    }

    const actionSequenceName = activeState.actionSequenceName;

    instructionState.subInstructionState.activeStateName = fsmStateName;
    instructionState.status = this.getSequence(actionSequenceName).state;

    fsmInstruction.setState(instructionState);

  }
}


Application.prototype.getApplicationVersionHash = function () {
  const clone = this.toJson();
  delete clone.uuid;

  const hash = utils.generateHashFromString(JSON.stringify(clone));
  return hash;
};

/**
 * Create a version hash and use it as the mUUID
 */
Application.prototype.setVersionHash = function () {
  this.mUUID = this.getApplicationVersionHash().toString();
};

/**
 * @returns {Array<string>} Names of the sequences.
 */
Application.prototype.listSequences = function() {
  return this.mSequences.map(function(pSequence) { return pSequence.getName(); });
};

Application.prototype.uniqueSequenceName = function(pSeqName) {
  let _lSeqName = pSeqName;
  let _lCounter = 0;
  const _lList = this.listSequences();

  while(_lList.includes(_lSeqName)) {
    _lCounter += 1;
    _lSeqName = pSeqName + ' (' + _lCounter + ')';
  }
  return _lSeqName;
};

/**
 * From the sequence name, get the sequence.
 * @param {string} pSeqName
 * @returns {Sequence}
 */
Application.prototype.getSequence = function(pSeqName) {
  return this.mSequences.find(function(pSequence) { return pSequence.getName() == pSeqName; });
};

Application.prototype.getSequenceByIndex = function(pIndex) {
  if (pIndex < 0 || pIndex >= this.mSequences.length) {
    return undefined;
  }

  return this.mSequences[pIndex];
};

Application.prototype.getParametersObject = function() {
  return this.mParameters;
};

Application.prototype.listAllApplicationParameterNames = function() {
  return Object.values(this.mParameters.mParameters).map((pAppParam) => pAppParam.name);
};

Application.prototype.getParameterObjectByName = function (pName) {
  const _lParams = this.mParameters.mParameters;
  for (const id in _lParams) {
    if (_lParams[id].name == pName) {
      return _lParams[id];
    }
  }
};

Application.prototype.setParameters = function(pParameters) {
  this.mParameters = pParameters;
};

Application.prototype.addSequence = function(pData) {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  const _lNewSequence = new Sequence(this.mLibrary, this, pData);
  const _lExistingSequence = this.getSequence(_lNewSequence.getName());

  // Sequence of that name already exists
  if(_lExistingSequence) {
    const _lNewName = this.uniqueSequenceName(_lNewSequence.getName());
    console.warn('Application [' + this.getName() + '] already has sequence [' + _lNewSequence.getName() + ']. Renaming to [' + _lNewName + ']');
    _lNewSequence.setName(_lNewName);
  }
  this.mSequences.push(_lNewSequence);
  this.setDirty();
  this.notify('onSequenceAdded', _lNewSequence);
  return _lNewSequence;
};

Application.prototype.getCurrentSequence = function() { return this.mCurrentSequence; };

Application.prototype.selectSequenceByName = function(pSequenceName, pForceRefresh = false) {
  if (pSequenceName == null) {
    if (this.mCurrentSequence) this.mCurrentSequence.setSelectedInstruction(null);
    this.mCurrentSequence = null;
    this.notify('onSequenceSelected', null);
    return;
  }

  const lSelectedSequence = this.getSequence(pSequenceName);
  if (this.mCurrentSequence != lSelectedSequence || pForceRefresh) {
    this.mCurrentSequence && this.mCurrentSequence.setSelectedInstruction(null);
    this.mCurrentSequence = lSelectedSequence;
    this.notify('onSequenceSelected', this.mCurrentSequence);
  }
};

// Note (AJ): This function is used by assembler to initialize Library
Application.prototype.setAppParameters = function (pAppParams) {
  if (pAppParams) {
    this.mParameters = this.addParameters(this, { parameters: pAppParams });
    this.setDirty();
  }
};

/**
 * Add a new ApplicationParameter object
 * @param {Application} pApplication - Application payload
 */
Application.prototype.addParameters = function(pApplication, pApplicationData) {
  return new ApplicationParameters(pApplication, pApplicationData);
};

/**
 * Add a new application parameter
 * @returns void
 */
Application.prototype.addApplicationParameter = function(pType) {
  return this.mParameters.addParameter(pType);
};


/**
 * Update an application parameter
 * @param {String} pParameterId - The application parameter name
 * @param {{ name: String, default_value: String}} pPayload - The payload for update
 * @returns void
 */
Application.prototype.updateApplicationParameter = function (pParameterId, pPayload) {
  this.mParameters.updateParameter(pParameterId, pPayload);
};

Application.prototype.getAllInstructions = function () {
  return this.listSequences().map(pSequenceName => this.getSequence(pSequenceName)).flatMap(pSeq => pSeq.listInstructions());
};

Application.prototype.isUnpackVariableUsed = function (pUnpackVariableName) {
  return this.getAllInstructions().some(pInstruction => {
    const pParamsCompatibleByUnpackVariables = pInstruction._getInstructionModel().parameters
      .filter(pParam => pParam.hasOwnProperty('acceptedVariableTypes') || pParam.name == 'expression' || pParam.name == 'conditionExpression' || pParam.name == 'conditions' || pParam.name == 'message' || pParam.name == 'variableName');
    return pParamsCompatibleByUnpackVariables.some(pParam => {
      const lParamValue = pInstruction.getParameter(pParam.name);
      if (typeof (lParamValue) == 'string') { return lParamValue.includes(pUnpackVariableName); }
      return false;
    });
  });
};

Application.prototype.removeOrUpdateUnpackVariable = function (pUnpackVariableName) {
  const lUnPackVariable = this.getParameterObjectByName(pUnpackVariableName);
  if (this.isUnpackVariableUsed(pUnpackVariableName)) {
    this.updateApplicationParameter(lUnPackVariable.id, { default_value: `"DELETED_UNPACK_VARIABLE"`, type: 'const' });
    return true;
  }
  else {
    this.removeApplicationParameter(lUnPackVariable.id);
    return false;
  }
};

/**
 * Remove application parameter
 * @param {String} pParameterId
 * @returns void
 */
Application.prototype.removeApplicationParameter = function(pParameterId) {
  this.mParameters.removeParameter(pParameterId);
  this.notify('onApplicationParameterRemoved', pParameterId);
};

Application.prototype.newSequence = function() {
  return this.addSequence({
    name : this.uniqueSequenceName('Sequence'),
    option : { type: 'Sequence' },
    instructions : []
  });
};

Application.prototype.removeSequence = function(pSequenceName) {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  const _lSeq = this.getSequence(pSequenceName);
  const _lIndex = this.mSequences.indexOf(_lSeq);

  if(_lSeq) {
    if(_lSeq != this.mInitialSequence) {
      this.mSequences.splice(_lIndex, 1);
      this.setDirty();
      this.notify('onSequenceRemoved', _lSeq);
      if (this.getCurrentSequence() == _lSeq) {
        this.selectSequenceByName(this.getInitialSequence().getName());
      }
    } else {
      console.warn('Application [' + this.getName() + '] can\'t remove initial sequence [' + pSequenceName + ']');
    }
  } else {
    console.warn('Application [' + this.getName() + '] does not have sequence [' + pSequenceName + ']');
  }
};

Application.prototype.moveSequence = function(pFrom, pTo) {
  if(pFrom >= 0 && pFrom < this.mSequences.length && pTo >= 0 && pTo < this.mSequences.length) {
    this.mSequences.splice(pTo, 0, this.mSequences.splice(pFrom, 1)[0]);
    this.setDirty();
    this.notify('onSequenceMoved', pFrom, pTo);
  }
};

Application.prototype.getInitialSequence = function() { return this.mInitialSequence; };

Application.prototype.setInitialSequence = function(pSequenceName) {
  const _lSeq = this.getSequence(pSequenceName);
  if(_lSeq) {
    // Initializing
    if(!this.mInitialSequence) {
      this.mInitialSequence = _lSeq;

      // Changing
    } else {
      this.mInitialSequence = _lSeq;
      this.setDirty();
      this.notify('onInitialSequenceChanged', this.mInitialSequence);
    }
  } else {
    console.warn('Application [' + this.getName() + '] does not have sequence [' + pSequenceName + ']');
  }
};

Application.prototype.transition = function({type, payload}) {
  const pApplication = this;

  function robotParametersArraysUpdate({ newArrays }) {
    pApplication.robotParameters.arrays = newArrays;
    pApplication.setDirty();
  }

  function robotLoopInstructionUpdate({ arrayId, instruction }) {
    instruction.setParameter('iteratorSymbol', 'iExecutionReferenceFrame');
    const selectedArray = pApplication.robotParameters?.arrays?.find(array => (arrayId === array.id)) || pApplication.robotParameters?.arrays[0];
    if ( selectedArray ) {
      instruction.setParameter('arrayId', selectedArray.id);
      instruction.setParameter('arrayName', selectedArray.patternConfig.name);
      const referenceFrames = ArrayOfArrayReferenceFrame__fromArrayOfPoint2DxExecutionReferenceFrame(selectedArray.arrayArrayPoint2D, selectedArray.arrayMatrixReferenceFrame);
      instruction.setParameter('arrayValue', referenceFrames);
      pApplication.setDirty();
    }
  }

  function robotLoopsInstructionUpdate() {
    for(const sequence of pApplication.mSequences) {
      for (const instruction of sequence.mInstructions) {
        instruction.mCategory === 'robotLoop' && instruction.mName === 'array' &&
        robotLoopInstructionUpdate({
          instruction: instruction,
          arrayId: instruction.getParameter('arrayId')
        });
      }
    }
  }

  switch (type) {
    case 'robot_arrays/onArraysNameUpdated':
      robotParametersArraysUpdate(payload);
      break;
    case 'robot_arrays/onRegenerateClick':
    case 'robot_arrays/onEditFrameDoneClick':
    case 'robot_arrays/onDeleteClick':
    case 'robot_arrays/onCopyClick':
      robotParametersArraysUpdate(payload);
      robotLoopsInstructionUpdate();
      break;
    case 'robot_loop_instruction/onArraySelected':
      robotLoopInstructionUpdate(payload);

      break;
    default:
      break;
  }
};

Application.prototype.getUsedTopicList = function() {
  const lSequenceList = this.mSequences;
  const lTopicList = [];
  lSequenceList.length > 0 && lSequenceList.forEach(function(pSequence){
    const lInstructionList = pSequence.listInstructions();
    lInstructionList.length > 0 && lInstructionList.filter(pInstruction => (pInstruction.getName() == 'event' && pInstruction.getCategory() == 'waitFor'))
      .forEach(function(pInstruction){
        pInstruction.getParameter('topic') != "" && !lTopicList.includes(pInstruction.getParameter('topic')) && lTopicList.push(pInstruction.getParameter('topic'));
      });
  });
  return lTopicList;
};

Application.prototype.getUsedMessageList = function() {
  const lSequenceList = this.mSequences;
  const lMessageList = [];
  lSequenceList.length > 0 && lSequenceList.forEach(function(pSequence){
    const lInstructionList = pSequence.listInstructions();
    lInstructionList.length > 0 && lInstructionList.filter(pInstruction => (pInstruction.getName() == 'event' && pInstruction.getCategory() == 'waitFor'))
      .forEach(function(pInstruction){
        pInstruction.getParameter('message') != "" && !lMessageList.includes(pInstruction.getParameter('message')) && lMessageList.push(pInstruction.getParameter('message'));
      });
  });
  return lMessageList;
};

/**
 * Check if the application has in its sequences, one that hasRobotSimulateInstruction returning true.
 * @returns {String}
 */
Application.prototype.hasRobotSimulateInstruction = function () {
  return this.mSequences.some(s => s.hasRobotSimulateInstruction());
};

Application._fromJsonInternal = function(pApplication, pLibrary, pJson) {
  ApplicationBase.fromJson(pApplication, pJson);
  pApplication.mVersion = pJson.version;

  pApplication.mSequences = [];
  pJson.sequences.forEach(function(pSequence) {
    pApplication.mSequences.push(new Sequence(pLibrary, pApplication, pSequence));
  });
  pApplication.setInitialSequence(pJson.initialSequence);
  pApplication.mBreakpoints = simpleClone(pJson.breakpoints);
  pApplication.mParameters = pApplication.addParameters(pApplication, pJson);
  /** @type {import('@ventionco/mm-machine-logic-robot').RobotParameters} */
  pApplication.robotParameters = simpleClone(pJson.robotParameters);
  pApplication.autoLaunch = pJson.autoLaunch || false;
  const robotAppsService =  pApplication.mLibrary.getRobotAppsService();
  robotAppsService.send('Application.prototype.fromJson', { applicationId: pApplication.mId, referenceFrames: pJson.robotParameters.calibrationParts });
};

Application.fromJson = function(pLibrary, pJson) {
  // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
  const lApplication = new Application(pLibrary);
  this._fromJsonInternal(lApplication, pLibrary, pJson);
  return lApplication;
};

Application.prototype.toJson = function() {
  const _lThis = this;

  const robotAppsService =  this.mLibrary.getRobotAppsService();

  return {
    ...ApplicationBase.prototype.toJson.call(this),
    version : this.mVersion,
    sequences : Object.keys(this.mSequences).map(function(pKey) { return _lThis.mSequences[pKey].toJson(); }),
    initialSequence : (this.mInitialSequence ? this.mInitialSequence.getName() : null),
    breakpoints : this.mBreakpoints,
    uuid : this.mUUID,
    parameters: this.mParameters && this.mParameters.toJson(),
    robotParameters: machineLogicApplicationRobotParameters_updateWithRobotAppsMachineContext(environment, robotAppsService.selectors)(this.mId, this.robotParameters)
  };
};

Application.prototype.getObjectType = function() { return 'Application'; };

Application.prototype.setRobotParametersClips = function(newRobotClips) {
  this.robotParameters = {
    ...this.robotParameters,
    clips: newRobotClips
  };
};

Object.defineProperty(
  Application.prototype,
  'validationErrorCount',
  {
    get: function () {
      return this.mLibrary.validator.cache[this.mName]?.length || 0;
    }
  }
);

Object.defineProperty(
  Application.prototype,
  'invalidInstructionUuids',
  {
    get: function () {
      const validationErrors = this.mLibrary.validator.cache[this.mName] || [];

      return validationErrors
        .filter(validationError => validationError.instruction?.mUuid !== undefined)
        .map(validationError => validationError.instruction.mUuid);
    }
  }
);

Object.defineProperty(
  Application.prototype,
  'parameters',
  {
    get: function () {
      return this.getParametersObject();
    }
  }
);

Object.defineProperty(
  Application.prototype,
  'currentSequence',
  {
    get: function () {
      return this.getCurrentSequence();
    }
  }
);

Object.defineProperty(
  Application.prototype,
  'initialSequence',
  {
    get: function () {
      return this.getInitialSequence();
    }
  }
);

Object.defineProperty(
  Application.prototype,
  'sequences',
  {
    get: function () {
      return this.mSequences;
    }
  }
);
